<template>
    <div class="w-full my-4">
        <div class="w-full mt-2">
            <h1 class="text-3xl capitalize">
                Historial de registro de inventarios
                {{ selectedMerchant.merchantType | merchantInfo("type") }}
                <strong class="text-theme-primary">
                    {{ selectedMerchant.name }}
                </strong>
            </h1>
        </div>

        <div class="mt-3 w-full">
            <time-filters v-model="query" />
            <div class="flex justify-center">
                <app-button v-show="query" relief @click="listItems">
                    Consultar 🔎
                </app-button>
            </div>
        </div>

        <div class="w-full mt-3">
            <custom-table
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                responsive
                outlined
                no-border-collapse
                hover
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
            >
                <template #cell(id)="data">
                    <div class="flex items-center">
                        <router-link
                            :to="`/app/inventories/input-detail/${data.item.id}?merchantId=${selectedMerchant.merchantId}&merchantType=${selectedMerchant.merchantType}`"
                            class="text-center btn btn-stack-primary p-1"
                        >
                            <span>
                                {{ data.item.id }}
                            </span>
                        </router-link>
                    </div>
                </template>

                <template #cell(barcode)="data">
                    <router-link
                        :to="`/app/products/detail/${data.item.productId}?reference=${data.item.id}&tab=references`"
                        class="text-center btn btn-stack-primary"
                    >
                        <span class="text-center">
                            {{ data.item.barcode || `ID ${data.item.id}` }}
                        </span>
                    </router-link>
                </template>

                <template #cell(sku)="data">
                    <router-link
                        :to="`/app/products/detail/${data.item.productId}`"
                        class="text-center btn btn-stack-primary"
                    >
                        <span class="text-center">
                            {{ data.item.sku || `ID ${data.item.productId}` }}
                        </span>
                    </router-link>
                </template>

                <template #cell(name)="data">
                    <button
                        icon
                        circle
                        class="btn-a"
                        @click="goToMainPage(data.item.slug)"
                    >
                        <span class="text-center">
                            {{ data.item.name }}
                        </span>
                    </button>
                </template>

                <template #cell(size)="data">
                    <span class="text-center capitalize">
                        {{ data.item.size }}
                    </span>
                </template>

                <template #cell(color)="data">
                    <span class="text-center capitalize">
                        {{ data.item.color }}
                    </span>
                </template>

                <template #cell(priceOffer)="data">
                    <div class="flex flex-col">
                        <span class="text-center">
                            {{ data.item.priceOffer | money }}
                        </span>
                        <span class="text-center line-through">
                            {{ data.item.price | money }}
                        </span>
                    </div>
                </template>

                <template #cell(costUnit)="data">
                    <span class="text-center">
                        {{ data.item.costUnit | money }}
                    </span>
                </template>

                <template #cell(supplier)="data">
                    <span class="text-center">
                        {{
                            data.item.supplier ? data.item.supplier.name : "NA"
                        }}
                    </span>
                </template>

                <template #cell(createdAt)="data">
                    <span class="text-center">
                        {{ data.item.createdAt | date }}
                    </span>
                </template>
            </custom-table>
            <div class="w-full flex justify-center">
                <app-button icon @click="downloadExcelComponent">
                    ⏬ Descargar excel
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters, mapState } from "vuex";

import { downloadExcel } from "@/common/lib/excel";

import CustomTable from "@/components/utils/CustomTable.vue";
import TimeFilters from "@/components/utils/TimeFilters.vue";

export default {
    name: "HistoryInventories",
    components: { TimeFilters, CustomTable },
    mixins: [urlQuerySyncMixin],
    data: () => ({
        itemsCurrent: [],
        $fields: [
            { key: "id", label: "ID", sortable: true },
            { key: "barcode", label: "Código de barras", sortable: true },
            { key: "sku", label: "Sku", sortable: true },
            { key: "name", label: "Nombre", sortable: true },
            { key: "costUnit", label: "Costo unidad", sortable: false },
            { key: "quantity", label: "Stock registradas", sortable: false },
            { key: "priceOffer", label: "Precio actual", sortable: false },
            { key: "createdAt", label: "Fecha de registro", sortable: true },
            { key: "quantity", label: "Stock registradas", sortable: false }
        ],
        isLoading: false,
        query: {},
        $urlFilterSchema: [
            { key: "begin", type: "string" },
            { key: "end", type: "string" },
            { key: "agoDays", type: "number" },
            { key: "agoMonths", type: "number" }
        ]
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {
        selectedMerchant() {
            this.listItems();
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("inventory:ListInput", "/app", true)))
            return;
        this.listItems();
    },
    methods: {
        ...mapActions("inventories", ["listInputsInventory"]),
        async listItems() {
            this.isLoading = true;
            this.$loading.show();
            try {
                const inventory = await this.listInputsInventory({
                    query: this.query,
                    merchantId: this.selectedMerchant.merchantId,
                    merchantType: this.selectedMerchant.merchantType
                });
                this.itemsCurrent = inventory;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        downloadExcelComponent() {
            const filename = `inventories_${this.selectedMerchant.name.replaceAll(
                " ",
                "_"
            )}`;
            downloadExcel({ rows: this.itemsCurrent, filename });
        },
        goToMainPage(slug) {
            window.open(`${this.$publicUrl}/${slug}`, "_blank").focus();
        }
    }
};
</script>
